import { useEffect, useRef, useState } from "react"
import { Code2, ListTree } from "lucide-react"
import { cn } from "@/lib/cn"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import {
  defaultGroup,
  fromJson,
  hydrateAst,
  toJson,
  type CustomFieldCatalog,
  type GroupNode,
} from "@/lib/predicate-ast"
import { PredicateGroup } from "./predicate-group"

// Top-level wrapper for the predicate builder. Owns:
//   - the in-memory AST (re-hydrated from `value` whenever it changes
//     externally — Inertia reload, Cancel, etc.)
//   - AST ↔ JSON mode toggle
//   - the "+ Match on source_state" quick-add chip
//   - a malformed-banner when JSON-mode editing produces an unparseable
//     tree
//
// The parent (PolicyEditorPanel) hands us the current JSON value and an
// `onChange(json)` callback. We never mutate `value` in place — every
// edit emits a new JSON object the parent can stash in its LocalBuffer.

type Mode = "ast" | "json"

type Props = {
  value: Record<string, unknown> | null | undefined
  catalog: CustomFieldCatalog
  onChange: (next: Record<string, unknown>) => void
  disabled?: boolean
}

export function PredicateBuilder({ value, catalog, onChange, disabled }: Props) {
  const [mode, setMode] = useState<Mode>("ast")
  const [malformed, setMalformed] = useState<string | null>(null)
  const [jsonText, setJsonText] = useState<string>(() => stringify(value))

  // The hydrated AST that our nested components edit. Re-derived from
  // `value` whenever it changes externally (Inertia reload, Cancel
  // revert, JSON-mode edit) — but NOT when the change originated from
  // this component, since re-parsing regenerates node ids and React
  // would unmount the input mid-keystroke and steal focus.
  const [ast, setAst] = useState<GroupNode>(() => parseToRoot(value, catalog))
  const lastEmittedRef = useRef<string | null>(null)

  useEffect(() => {
    const incoming = stringify(value)
    if (incoming === lastEmittedRef.current) return
    const next = parseToRoot(value, catalog)
    setAst(next)
    setMalformed(null)
    setJsonText(incoming)
    lastEmittedRef.current = null
  }, [value, catalog])

  // Whenever the AST changes, push the JSON shape upstream. We stash
  // the emitted text so the round-trip useEffect above can ignore the
  // echoing prop change without rebuilding the AST.
  const emit = (next: GroupNode) => {
    setAst(next)
    const json = toJson(next)
    const text = stringify(json)
    lastEmittedRef.current = text
    onChange(json)
    setJsonText(text)
  }

  // JSON-mode editing — operator hand-edits the tree. We parse on every
  // keystroke and only push upstream on parse-success; on parse-failure
  // we surface the malformed banner.
  const handleJsonChange = (text: string) => {
    setJsonText(text)
    if (text.trim() === "") {
      onChange({})
      setMalformed(null)
      return
    }
    let parsed: unknown
    try {
      parsed = JSON.parse(text)
    } catch (e) {
      setMalformed(e instanceof Error ? e.message : "Invalid JSON")
      return
    }
    const result = fromJson(parsed)
    if (!result.ok) {
      setMalformed(result.reason)
      return
    }
    setMalformed(null)
    if (typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)) {
      onChange(parsed as Record<string, unknown>)
    } else {
      onChange({})
    }
  }

  return (
    <div className="space-y-3">
      {/* Header strip — mode toggle on the right */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-[11px] text-app-muted">
          <span>Match conditions on appuser facts and conversation facts.</span>
        </div>
        <div className="inline-flex items-center gap-1 rounded-control border border-border bg-white/5 p-0.5 text-[11px]">
          <button
            type="button"
            onClick={() => setMode("ast")}
            className={cn(
              "inline-flex items-center gap-1 rounded-control px-2 py-1 transition",
              mode === "ast" ? "bg-app-panel2 text-app-text" : "text-app-muted hover:text-app-text",
            )}
          >
            <ListTree size={11} />
            Builder
          </button>
          <button
            type="button"
            onClick={() => setMode("json")}
            className={cn(
              "inline-flex items-center gap-1 rounded-control px-2 py-1 transition",
              mode === "json" ? "bg-app-panel2 text-app-text" : "text-app-muted hover:text-app-text",
            )}
          >
            <Code2 size={11} />
            JSON
          </button>
        </div>
      </div>

      {/* Malformed banner */}
      {malformed && (
        <div className="rounded-control border border-status-error/40 bg-status-error/10 px-3 py-2 text-[11px] text-status-error">
          Malformed predicate: {malformed}
        </div>
      )}

      {/* Body — builder or raw JSON */}
      {mode === "ast" ? (
        <PredicateGroup
          group={ast}
          catalog={catalog}
          depth={0}
          onChange={emit}
          disabled={disabled}
        />
      ) : (
        <div className="space-y-2">
          <Textarea
            value={jsonText}
            onChange={(e) => handleJsonChange(e.target.value)}
            disabled={disabled}
            spellCheck={false}
            rows={Math.min(18, Math.max(6, jsonText.split("\n").length))}
            className="font-mono text-xs"
          />
          <div className="flex items-center justify-between text-[10px] text-app-subtle">
            <span>
              Edit the predicate JSON directly. The tree must match the shape{" "}
              <code className="text-app-muted">{`{ all: [{ "appuser.custom_fields.tier": { eq: "gold" }}, …]}`}</code>.
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleJsonChange(stringify(toJson(defaultGroup("all"))))}
              disabled={disabled}
            >
              Reset to empty
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

function parseToRoot(
  value: Record<string, unknown> | null | undefined,
  catalog: CustomFieldCatalog,
): GroupNode {
  const parsed = fromJson(value ?? {})
  if (!parsed.ok) return defaultGroup("all")
  const hydrated = hydrateAst(parsed.ast, catalog)
  if (hydrated.kind === "group") return hydrated
  // Wrap a stray top-level leaf into a group; shouldn't happen post
  // fromJson but keeps the type narrow.
  return { ...defaultGroup("all"), children: [hydrated] }
}

function stringify(value: unknown): string {
  if (value === null || value === undefined) return "{}"
  return JSON.stringify(value, null, 2)
}
